// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// =============================================================================
// SchoolAutocomplete.jsx — school name lookup, filtered by state
// =============================================================================
// Renders: a text input with a suggestion dropdown of schools from the
//          `schools` table, scoped to the user's state (or all states if
//          state is unset).
// Props:
//   value       - current school name
//   onChange    - called as the user types or picks a suggestion
//   state       - state code (QLD/VIC/etc.) used to filter the school list
//   placeholder - input placeholder
//   locked      - if true, render as read-only
// Notes: SCHOOL_EXCEPTIONS (in formConstants) lists names always shown
//        regardless of state filter (e.g. distance education).
// =============================================================================

import React from "react";
import { searchSchools } from "../../data/schools";
import { SCHOOL_EXCEPTIONS, schoolExcludeTypeForYearLevel } from "../../utils/formConstants";

// onChange(value)  — called as the user TYPES (an unconfirmed value).
// onConfirm(value, schoolId) — called when the value is a deliberate choice: a
//                    picked suggestion, a special-option chip, or an explicit
//                    "use this name" free-text confirm. `confirmed` styles the
//                    prompt (parent tracks the real confirmed flag). schoolId is
//                    the picked schools.id ONLY when the choice is a listed school
//                    (BYOD gate, db-198/db-199); it is null/undefined for a chip,
//                    "Other", or free-typed name.
function SchoolAutocomplete({
  value,
  onChange,
  onConfirm,
  confirmed,
  state,
  placeholder,
  locked,
  // yearLevel drives the phase filter (Prep–6 hide Secondary; 7–12 hide Primary) and
  // gates the "TAFE" chip. disabled locks the whole picker until Gender + Year Level
  // are chosen (John, 9 Aug 2026).
  yearLevel,
  disabled,
}) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [showDrop, setShowDrop] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  // The dropdown shows the first 10 matches; "Show more" reveals the rest of the
  // fetched batch. Reset on each new search.
  const [showAll, setShowAll] = React.useState(false);
  const PAGE = 10;
  // "Other" free-text mode: the school isn't in the list, so the user picked the
  // "Other" chip and now types the name freely — which is accepted (confirmed).
  const [otherMode, setOtherMode] = React.useState(false);
  const timerRef = React.useRef(null);
  // Confirm falls back to onChange when the parent didn't pass onConfirm.
  const confirm = onConfirm || onChange;
  // Only parents that opt in (pass onConfirm) get the "must choose properly" UX;
  // the legacy per-child tabs pass onChange only and keep their old behaviour.
  const usesConfirm = typeof onConfirm === "function";

  function doSearch(q) {
    if (!q || q.length < 2) {
      setSuggestions([]);
      setShowDrop(false);
      return;
    }
    setSearching(true);
    setShowAll(false); // a new search collapses back to the first 10
    // Phase filter by year level: Prep–6 hides Secondary; 7–12 hides Primary; TAFE/none none.
    const excludeType = schoolExcludeTypeForYearLevel(yearLevel);
    searchSchools(q, state, false, 30, excludeType).then(function (rows) {
      setSearching(false);
      if (rows.length > 0) {
        setSuggestions(rows);
        setShowDrop(true);
      } else {
        searchSchools(q, state, true, 30, excludeType).then(function (rows2) {
          setSuggestions(rows2);
          setShowDrop(rows2.length > 0);
        });
      }
    });
  }

  function handleInput(e) {
    const val = e.target.value;
    // In "Other" mode the typed name IS the school (confirmed); otherwise typing is
    // an unconfirmed search term and the user must pick a suggestion / chip.
    if (otherMode) {
      confirm(val);
      return;
    }
    onChange(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function () {
      doSearch(val);
    }, 280);
  }

  function handleSelect(school) {
    setOtherMode(false);
    // A listed school carries its schools.id — emit it so the parent can store the
    // precise school (BYOD gate). Chips / "Other" / free-typed names pass no id.
    confirm(school.name + (school.suburb ? ` — ${school.suburb}` : ""), school.id ?? null);
    setSuggestions([]);
    setShowDrop(false);
  }

  // The special-option chips. "Other" enters free-text mode (type your school's
  // name); the rest are a direct, confirmed choice.
  function handleChip(ex) {
    setSuggestions([]);
    setShowDrop(false);
    if (ex === "Other") {
      if (otherMode) {
        // Toggle back to search mode (e.g. clicked Other by mistake).
        setOtherMode(false);
        onChange("");
        return;
      }
      setOtherMode(true);
      confirm(""); // clear so they type the real name; empty stays "not chosen"
      return;
    }
    setOtherMode(false);
    confirm(ex);
  }

  // Guidance prompt: parent opted in, not confirmed, there's a typed search term,
  // and we're NOT in Other mode. No button — the user must pick a suggestion or a chip.
  const needsGuidance =
    usesConfirm && !otherMode && !confirmed && String(value || "").trim().length >= 2;

  // Show the first 10; "Show more" reveals the rest of the fetched batch.
  const shownSuggestions = showAll ? suggestions : suggestions.slice(0, PAGE);
  const moreCount = suggestions.length - shownSuggestions.length;

  if (locked) {
    return (
      <div
        style={{
          padding: "9px 13px",
          borderRadius: 7,
          background: "#F5F7F7",
          border: "1px solid #CFE0DE",
          fontSize: 14,
          color: "#1A2E2B",
          minHeight: 38,
        }}
      >
        {value || <span style={{ color: "#aaa", fontStyle: "italic" }}>-</span>}
      </div>
    );
  }

  // Locked until prerequisites: greyed, non-interactive, with a hint. Gender + Year
  // Level must be chosen before a school can be picked (John, 9 Aug 2026).
  if (disabled) {
    return (
      <input
        type="text"
        value=""
        disabled
        placeholder="Choose Gender and Year Level first"
        style={{
          width: "100%",
          padding: "9px 13px",
          borderRadius: 7,
          border: "1px solid #E0E0E0",
          fontSize: 14,
          color: "#9AA5A2",
          fontFamily: "inherit",
          boxSizing: "border-box",
          background: "#F5F7F7",
          cursor: "not-allowed",
        }}
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onBlur={function () {
          setTimeout(function () {
            setShowDrop(false);
          }, 180);
        }}
        placeholder={placeholder || "Start typing school name..."}
        autoComplete="off"
        style={{
          width: "100%",
          padding: "9px 13px",
          borderRadius: 7,
          border: `1px solid ${"#CFE0DE"}`,
          fontSize: 14,
          color: "#1A2E2B",
          fontFamily: "inherit",
          boxSizing: "border-box",
          background: "#FFFFFF",
        }}
      />
      {searching && (
        <div
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            color: "#607D79",
          }}
        >
          searching...
        </div>
      )}
      {showDrop && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1px solid #ccc",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            marginTop: 2,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {shownSuggestions.map(function (s, idx) {
            return (
              <button
                type="button"
                key={idx}
                onMouseDown={function (e) {
                  // preventDefault stops the input from blurring before the
                  // click registers — same pattern as AddressAutocomplete.
                  e.preventDefault();
                  handleSelect(s);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "white",
                  border: "none",
                  borderBottom: "1px solid #f0f0f0",
                  padding: "9px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontFamily: "inherit",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                onMouseEnter={function (e) {
                  e.currentTarget.style.background = "#f5faf9";
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = "white";
                }}
              >
                <span>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  {s.suburb && (
                    <span style={{ color: "#888", marginLeft: 8, fontSize: 12 }}>{s.suburb}</span>
                  )}
                </span>
                {s.school_type && (
                  <span
                    style={{ fontSize: 11, color: "#90A4A0", marginLeft: 8, whiteSpace: "nowrap" }}
                  >
                    {s.school_type}
                  </span>
                )}
              </button>
            );
          })}
          {moreCount > 0 && (
            <button
              type="button"
              onMouseDown={function (e) {
                e.preventDefault();
                setShowAll(true);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "8px 14px",
                fontSize: 12,
                color: "#00695C",
                fontWeight: 600,
                borderTop: "1px solid #f0f0f0",
                background: "white",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ▾ Show {moreCount} more
            </button>
          )}
          <div
            style={{
              padding: "6px 14px",
              fontSize: 11,
              color: "#aaa",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            {usesConfirm
              ? 'Not listed? Try a different keyword, or choose "Other" below and type your school\'s name.'
              : "Not listed? Type the full name and continue."}
          </div>
        </div>
      )}
      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {SCHOOL_EXCEPTIONS.map(function (ex) {
          const activeChip = ex === "Other" ? otherMode : value === ex;
          // The TAFE chip is only selectable once Year Level = "TAFE" (John, 9 Aug 2026).
          const chipDisabled = ex === "TAFE" && yearLevel !== "TAFE";
          return (
            <button
              key={ex}
              type="button"
              disabled={chipDisabled}
              title={chipDisabled ? 'Choose Year Level "TAFE" first' : undefined}
              onMouseDown={function () {
                if (chipDisabled) return;
                handleChip(ex);
              }}
              style={{
                background: chipDisabled ? "#F0F0F0" : activeChip ? "#E0F2F1" : "#F5F5F5",
                border: `1px solid ${chipDisabled ? "#E0E0E0" : activeChip ? "#00897B" : "#E0E0E0"}`,
                borderRadius: 5,
                padding: "2px 10px",
                fontSize: 11,
                cursor: chipDisabled ? "not-allowed" : "pointer",
                color: chipDisabled ? "#BBBBBB" : activeChip ? "#00695C" : "#607D79",
                fontFamily: "inherit",
              }}
            >
              {ex}
            </button>
          );
        })}
      </div>
      {/* Other mode: the school isn't in the list — the user types its name here.
          Warn that "Other" should be a last resort (a free-typed name isn't linked
          to a school record, so downstream school-linked features don't apply). */}
      {otherMode && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "#7c4a00",
            background: "#FFF3E0",
            border: "1px solid #FFB300",
            borderRadius: 6,
            padding: "6px 10px",
          }}
        >
          ⚠ Only select <strong>"Other"</strong> if the school is not in the list. Type your
          school's name in the box above.
        </div>
      )}
      {/* Unconfirmed search text: no button — the user must pick a school from the
          list (try a different keyword), or choose "Other" to type a name. */}
      {needsGuidance && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "#7c4a00",
            background: "#FFF3E0",
            border: "1px solid #FFB300",
            borderRadius: 6,
            padding: "6px 10px",
          }}
        >
          ⚠ Select the school from the list — try a different keyword. If the school can't be found,
          choose <strong>"Other"</strong> and type in the name of your school.
        </div>
      )}
    </div>
  );
}

export { SchoolAutocomplete };
